/** Templates picker — Tier 3 Group D Slice 1c.
 *
 *  File → New from Template opens this dialog. Category tabs on
 *  the left, a card grid on the right showing each template at the
 *  active category. Selecting a card creates a new tab with a
 *  blank canvas at the template's dimensions (and optional DPI).
 *
 *  User-saved templates ("My templates" tab) get inline rename +
 *  delete affordances on each card. Built-in cards are read-only —
 *  Photoshop ships them similarly. */

import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { MODAL_OVERLAY, MODAL_SHEET_CHROME } from '../lib/modal-classes';
import {
  Camera,
  CreditCard,
  FileText,
  Globe,
  Hash,
  Image as ImageIcon,
  Laptop,
  LayoutTemplate,
  Mail,
  Megaphone,
  Monitor,
  Pencil,
  RectangleHorizontal,
  Smartphone,
  Tablet,
  Trash2,
  UserCircle,
  Video,
  Watch,
  X,
} from 'lucide-react';
import {
  TEMPLATE_CATEGORY_LABEL,
  type DocumentTemplate,
  type TemplateCategory,
} from '../lib/builtin-templates';
import { useTemplates } from '../lib/use-templates';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Per-template-id icon, used as a faint watermark inside each
 *  card's preview rect. Generic-but-semantic alternatives where
 *  lucide doesn't ship brand-specific marks (Instagram, Twitter
 *  etc. aren't in this lucide-react version) — Camera for
 *  Instagram-style image-share, Hash for Twitter / X, Globe for
 *  Facebook, Video for YouTube / TikTok, UserCircle for LinkedIn.
 *  Unknown / user templates fall back to a generic image icon. */
const TEMPLATE_ICON_BY_ID: Record<string, IconComponent> = {
  'builtin:ig-post': Camera,
  'builtin:ig-portrait': Camera,
  'builtin:ig-story': Camera,
  'builtin:twitter-post': Hash,
  'builtin:fb-cover': Globe,
  'builtin:yt-thumbnail': Video,
  'builtin:linkedin-banner': UserCircle,
  'builtin:tiktok': Video,
  'builtin:letter-300': FileText,
  'builtin:a4-300': FileText,
  'builtin:a5-300': FileText,
  'builtin:postcard-300': Mail,
  'builtin:business-card-300': CreditCard,
  'builtin:poster-300': Megaphone,
  'builtin:web-fhd': Monitor,
  'builtin:web-hd': Monitor,
  'builtin:web-qhd': Monitor,
  'builtin:web-4k': Monitor,
  'builtin:web-laptop': Laptop,
  'builtin:web-banner': RectangleHorizontal,
  'builtin:iphone-14': Smartphone,
  'builtin:iphone-se': Smartphone,
  'builtin:ipad-pro': Tablet,
  'builtin:pixel-7': Smartphone,
  'builtin:apple-watch': Watch,
  'builtin:macbook-pro-14': Laptop,
};

function iconForTemplate(template: DocumentTemplate): IconComponent {
  return TEMPLATE_ICON_BY_ID[template.id] ?? ImageIcon;
}

/** Reduce a width × height pair to the simplest integer ratio when
 *  one exists with both terms ≤ 20 (so we display nice "9:16" / "1:1"
 *  rather than "1170:2532"). Returns `null` when the ratio is too
 *  irregular to be useful, in which case the card omits the readout. */
function formatAspectRatio(w: number, h: number): string | null {
  if (w <= 0 || h <= 0) return null;
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(Math.round(w), Math.round(h));
  const a = Math.round(w / g);
  const b = Math.round(h / g);
  if (a > 20 || b > 20) return null;
  return `${a}:${b}`;
}

interface Props {
  onClose: () => void;
  /** Called with the selected template's resolved dimensions +
   *  optional DPI. The parent is responsible for spawning the new
   *  document — the dialog stays out of the editor's mutation
   *  pipeline. */
  onPick: (template: DocumentTemplate) => void;
}

export function TemplatePickerDialog({ onClose, onPick }: Props) {
  const { byCategory } = useTemplates();
  const firstCategory = byCategory[0]?.category ?? 'social';
  const [active, setActive] = useState<TemplateCategory>(firstCategory);

  const activeBucket = byCategory.find((b) => b.category === active);

  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={MODAL_OVERLAY} onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-label="New from template"
        aria-modal="true"
        className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full sm:w-[92vw] max-w-3xl max-h-[85vh] flex flex-col overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
          <LayoutTemplate className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold flex-1">New from Template</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex">
          <nav
            aria-label="Template categories"
            className="w-40 shrink-0 border-r border-border overflow-y-auto py-2"
          >
            {byCategory.map((bucket) => (
              <button
                key={bucket.category}
                type="button"
                aria-pressed={bucket.category === active}
                onClick={() => setActive(bucket.category)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  bucket.category === active
                    ? 'bg-accent text-foreground font-medium border-l-2 border-primary -ml-px'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                }`}
              >
                {bucket.label}
                <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">
                  {bucket.templates.length}
                </span>
              </button>
            ))}
          </nav>

          <div className="flex-1 overflow-y-auto p-4">
            {!activeBucket || activeBucket.templates.length === 0 ? (
              <EmptyCategoryHint category={active} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {activeBucket.templates.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    onPick={() => {
                      onPick(t);
                      onClose();
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyCategoryHint({ category }: { category: TemplateCategory }) {
  if (category === 'custom') {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        No saved templates yet. Use <strong>File → Save as Template…</strong> from any document to
        capture its size here.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground py-6 text-center">
      No templates in {TEMPLATE_CATEGORY_LABEL[category]}.
    </p>
  );
}

function TemplateCard({ template, onPick }: { template: DocumentTemplate; onPick: () => void }) {
  const { rename, remove } = useTemplates();
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(template.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const aspect = template.width / template.height;
  const isUserTemplate = template.kind === 'user';

  // Thumbnail preview — letterbox the template's aspect ratio
  // into a 16:9 card area. `min/max` clamps prevent extreme
  // aspects (e.g. business card 1.75:1, watch ~0.8:1) from
  // collapsing the card.
  const previewWidthPct = aspect >= 1 ? 90 : Math.max(40, aspect * 90);
  const previewHeightPct = aspect >= 1 ? Math.max(40, (1 / aspect) * 90) : 90;

  const Icon = iconForTemplate(template);
  const aspectLabel = formatAspectRatio(template.width, template.height);

  return (
    <div className="group border border-border rounded-md overflow-hidden hover:border-primary transition-colors">
      <button
        type="button"
        onClick={onPick}
        className="w-full bg-muted h-32 relative flex items-center justify-center"
        aria-label={`Use ${template.name} template`}
      >
        <div
          className="relative bg-background border border-border shadow-sm flex items-center justify-center"
          style={{
            width: `${previewWidthPct}%`,
            height: `${previewHeightPct}%`,
          }}
          aria-hidden="true"
        >
          <Icon className="text-muted-foreground/40 w-1/3 h-1/3 max-w-10 max-h-10" />
          {aspectLabel && (
            <span className="absolute bottom-0.5 right-1 text-[9px] text-muted-foreground/60 tabular-nums">
              {aspectLabel}
            </span>
          )}
        </div>
      </button>
      <div className="px-2.5 py-2 space-y-0.5">
        <div className="flex items-center gap-1">
          {editing ? (
            <input
              type="text"
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => {
                if (nameDraft.trim() && nameDraft !== template.name) {
                  rename(template.id, nameDraft);
                } else {
                  setNameDraft(template.name);
                }
                setEditing(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  setNameDraft(template.name);
                  setEditing(false);
                }
              }}
              className="flex-1 min-w-0 text-xs font-medium bg-transparent border-b border-border focus:border-primary focus:outline-none px-0.5"
              aria-label="Template name"
            />
          ) : (
            <span className="flex-1 min-w-0 truncate text-xs font-medium">{template.name}</span>
          )}
          {isUserTemplate && !editing && !confirmDelete && (
            <>
              <button
                type="button"
                aria-label={`Rename ${template.name}`}
                title="Rename"
                onClick={() => setEditing(true)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-opacity"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                aria-label={`Delete ${template.name}`}
                title="Delete"
                onClick={() => setConfirmDelete(true)}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-accent transition-opacity"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          )}
          {confirmDelete && (
            <>
              <button
                type="button"
                onClick={() => remove(template.id)}
                className="text-[10px] px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-[10px] px-1.5 py-0.5 rounded hover:bg-accent text-muted-foreground"
              >
                Cancel
              </button>
            </>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground tabular-nums">
          {template.width} × {template.height}
          {template.dpi ? ` · ${template.dpi} DPI` : ''}
          {template.description ? ` · ${template.description}` : ''}
        </div>
      </div>
    </div>
  );
}
